import type { Lifecycle } from './pageLabels';

/**
 * What a card and the editor offer, worked out in one place (design.md I2,
 * I3), so the card menu, the editor's buttons and a test cannot disagree.
 */

export type PageActionId = 'edit' | 'preview' | 'publish' | 'unpublish' | 'remove';

/** The card menu's words, in menu order. */
export const ACTION_LABELS: Record<PageActionId, string> = {
  edit: 'Edit',
  preview: 'Preview',
  publish: 'Publish',
  unpublish: 'Unpublish',
  remove: 'Remove',
};

/** Where a move can take a page: the backend's one verb (D6). */
export type MoveTarget = Exclude<Lifecycle, 'DRAFT'>;

/** Where each lifecycle action goes, as the backend's one verb takes it (D6). */
export const TRANSITIONS: Partial<Record<PageActionId, MoveTarget>> = {
  publish: 'PUBLISHED',
  unpublish: 'INACTIVE',
  remove: 'ARCHIVED',
};

/**
 * A card's menu (P10, I2): a removed page offers nothing; somebody who may
 * only look gets Preview (P22).
 */
export function actionsFor(
  row: { lifecycle: Lifecycle },
  writable: boolean,
): PageActionId[] {
  if (row.lifecycle === 'ARCHIVED') return [];
  if (!writable) return ['preview'];
  const move = row.lifecycle === 'PUBLISHED' ? 'unpublish' : 'publish';
  return ['edit', 'preview', move, 'remove'];
}

export type EditorButtonId =
  'cancel' | 'preview' | 'saveDraft' | 'saveChanges' | 'publish' | 'unpublish';

export const BUTTON_LABELS: Record<EditorButtonId, string> = {
  cancel: 'Cancel',
  preview: 'Preview',
  saveDraft: 'Save Draft',
  saveChanges: 'Save Changes',
  publish: 'Publish Page',
  unpublish: 'Unpublish',
};

/** Why Unpublish is off while the form has changes (P12). */
export const SAVE_FIRST = 'Save or discard your changes before you unpublish';

/** The alert on a removed page, which can no longer change (P12, backend I3). */
export const REMOVED_ALERT = 'This page was removed and can no longer be changed';

export interface EditorButton {
  id: EditorButtonId;
  disabled: boolean;
  /** Why the button is off, in words; null when it is on. */
  reason: string | null;
}

export interface EditorView {
  buttons: EditorButton[];
  /** Every field disabled. */
  readOnly: boolean;
  /** An alert above the form, or null. */
  alert: string | null;
}

const on = (id: EditorButtonId): EditorButton => ({ id, disabled: false, reason: null });

/**
 * The editor's buttons for a lifecycle, `NEW` for `/pages/new` (P12, I3).
 * A removed page is read-only with Cancel only; somebody who may only look
 * gets Cancel and Preview with every field disabled (P22).
 */
export function buttonsFor(
  lifecycle: Lifecycle | 'NEW',
  writable: boolean,
  dirty: boolean,
): EditorView {
  if (lifecycle === 'ARCHIVED') {
    return { buttons: [on('cancel')], readOnly: true, alert: REMOVED_ALERT };
  }
  if (!writable) {
    return { buttons: [on('cancel'), on('preview')], readOnly: true, alert: null };
  }
  const buttons = [on('cancel'), on('preview')];
  if (lifecycle === 'NEW' || lifecycle === 'DRAFT') {
    buttons.push(on('saveDraft'), on('publish'));
  } else if (lifecycle === 'PUBLISHED') {
    buttons.push(
      on('saveChanges'),
      dirty ? { id: 'unpublish', disabled: true, reason: SAVE_FIRST } : on('unpublish'),
    );
  } else {
    buttons.push(on('saveChanges'), on('publish'));
  }
  return { buttons, readOnly: false, alert: null };
}
